












































import { Component, Mixins, Watch } from "vue-property-decorator";

import AppContent from "@/components/AppContent/AppContent.vue";
import AppHeader from "@/components/AppHeader/AppHeader.vue";
import AppHeaderMain from "@/components/AppHeader/AppHeaderMain.vue";
import ClassesTable from "@/components/ClassesTable/ClassesTable.vue";
import TableToolbar from "@/components/TableToolbar/TableToolbar.vue";
import StickyListHeader from "@/components/StickyListHeader/StickyListHeader.vue";

import BaseSpinner from "@/components/BaseSpinner/BaseSpinner.vue";
import BaseAlert from "@/components/BaseAlert/BaseAlert.vue";
import BaseDropdown from "@/components/BaseDropdown/BaseDropdown.vue";

import RequestState from "@/utils/mixins/RequestState";

import { Action, State } from "vuex-class";
import { IVuexState } from "@/models/interfaces/store";
import { ISchool } from "@/models/interfaces/schools";
import { IClass, ISubgroup } from "@/models/interfaces/classes";
import { IStudent } from "@/models/interfaces/students";
import { ClassesTable as ClassesTableType } from "@/models/types/classes";
import { ITeacher } from "@/models/interfaces/teachers";

@Component({
  components: {
    AppContent,
    AppHeader,
    AppHeaderMain,
    ClassesTable,
    TableToolbar,
    StickyListHeader,

    BaseSpinner,
    BaseAlert,
    BaseDropdown
  }
})
export default class ClassesListView extends Mixins(RequestState) {
  @Action("classes/fetchClassesForSchool")
  private fetchClassesForSchool!: (
    schoolsIds: string[]
  ) => Promise<{ classes: IClass[] }>;

  @Action("classes/fetchTeachersForClass")
  private fetchTeachersForClass!: (classId: string) => Promise<ITeacher[]>;

  @Action("classes/fetchStudentsInClass")
  private fetchStudentsInClass!: (classId: string) => Promise<IStudent[]>;

  @Action("schools/fetchSchools")
  private fetchSchools!: () => Promise<{ schools: ISchool[] }>;

  @State("students", { namespace: "classes" })
  private classStudents!: IVuexState<IStudent[]>;

  @State("schools", { namespace: "schools" })
  private schools!: IVuexState<ISchool[]>;

  @State("classes", { namespace: "classes" })
  private classes!: IVuexState<IClass[]>;

  @State("classTeachers", { namespace: "classes" })
  private classTeachers!: IVuexState<ITeacher[]>;

  private schoolId = "";
  private tableData: ClassesTableType = [];

  private get showSchoolsDropdown() {
    return (
      this.hasSucceeded(this.schools) &&
      !this.isEmpty(this.schools) &&
      this.schools.data!.length > 1
    );
  }

  private get schoolsOptions() {
    return this.schools.data?.map(({ schoolName, schoolId }) => ({
      label: schoolName,
      value: schoolId
    }));
  }

  private async generateTableData() {
    const normalizedClasses =
      this.classes.data?.map(group => [group, group.subGroups]) ?? [];

    for (const [g, subGroups] of normalizedClasses) {
      const group = g as IClass;
      const relatedSubgroups = subGroups as ISubgroup[];

      await Promise.all([
        this.fetchStudentsInClass(group.classId),
        this.fetchTeachersForClass(group.classId)
      ]);

      const teachers = this.isEmpty(this.classTeachers)
        ? `Unassigned`
        : this.classTeachers.data!.length > 1
        ? `${this.classTeachers.data!.length} teachers`
        : this.classTeachers.data![0].username;

      const addGroupToTable = () => {
        this.tableData.push({
          subGroup: null,
          className: group.className,
          teachers,
          students: this.classStudents.data?.length ?? 0,
          assignedLesson: group.assignedLesson?.title ?? "",
          groupId: group.classId,
          id: group.classId
        });
      };

      if (!relatedSubgroups.length) {
        addGroupToTable();
        continue;
      }

      addGroupToTable();

      relatedSubgroups.forEach(subgroup => {
        this.tableData.push({
          subGroup: subgroup.name,
          className: group.className,
          teachers,
          students: subgroup.studentCount,
          assignedLesson: subgroup.assignedLesson?.title ?? "",
          groupId: group.classId,
          id: subgroup.subGroupId
        });
      });
    }
  }

  public async mounted() {
    await this.fetchSchools();

    if (!this.isEmpty(this.schools)) {
      this.schoolId = this.schools.data![0].schoolId;
    }
  }

  @Watch("schoolId")
  private async onSchoolChanged(id: string) {
    await this.fetchClassesForSchool([id]);

    if (!this.isEmpty(this.classes)) {
      this.generateTableData();
    }
  }

  @Watch("classes.data")
  private async onClassesListChanged(data: IClass[]) {
    // when some classes/class has/have been deleted

    this.tableData = this.tableData.filter(({ id }) => {
      const existsInStore = data.find(item => item.classId === id);

      return existsInStore;
    });
  }
}
